// src/pages/Register.tsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../store/session";
import { useI18n } from "../i18n";
import { resendVerification } from "../api/auth";
import { ApiError } from "../api/http";

const RegisterPage: React.FC = () => {
  const { register } = useSession();
  const { t } = useI18n();

  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [sent, setSent] = useState(false);
  const [emailHint, setEmailHint] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSent(false);
    setEmailHint(null);

    try {
      const res = await register(email, password, displayName || undefined);
      setEmailHint(res?.emailHint ?? null);
      setSent(true);
      // Do NOT navigate. User must verify email first.
    } catch (err: any) {
      if (err instanceof ApiError) {
        if (err.status === 409 && err.code === "email_exists") {
          setError(t("auth.emailExists") || "This email is already registered.");
        } else if (err.status === 429) {
          setError(t("common.rateLimited") || "Too many attempts. Try again later.");
        } else {
          setError(err.message || "Registration failed");
        }
      } else {
        setError(err?.message || "Registration failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setError(null);
    try {
      await resendVerification(email);
      setSent(true);
    } catch (err: any) {
      if (err instanceof ApiError && err.status === 429) {
        setError(t("common.rateLimited") || "Too many attempts. Try again later.");
      } else {
        setError(err?.message || "Failed to resend verification email");
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-slate-900/80 border border-slate-800 rounded-2xl shadow-2xl p-6 space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-2xl bg-pink-500/20 border border-pink-500/50 flex items-center justify-center text-pink-300 font-bold text-sm">
            IS
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-pink-300">
              IntimSync
            </div>
            <h1 className="text-lg font-semibold text-slate-50 mt-1">
              {t("auth.registerTitle")}
            </h1>
            <p className="text-xs text-slate-400 mt-1">
              {t("auth.registerSubtitle")}
            </p>
          </div>
        </div>

        {sent && (
          <div className="text-xs text-emerald-300 bg-emerald-950/30 border border-emerald-800 rounded-lg px-3 py-2 space-y-2">
            <div>
              {t("auth.verificationSent") || "Verification email sent."}
              {emailHint ? (
                <span className="text-slate-200"> ({emailHint})</span>
              ) : null}
            </div>
            <div className="text-slate-300">
              {t("auth.checkInbox") || "Check your inbox and click the link to activate your account."}
            </div>

            <button
              type="button"
              onClick={handleResend}
              className="w-full mt-1 inline-flex items-center justify-center rounded-xl border border-slate-700 text-slate-200 text-sm py-2 hover:border-slate-500"
            >
              {t("auth.resendVerification") || "Resend verification email"}
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-300">
              {t("common.email")}
            </label>
            <input
              type="email"
              required
              autoComplete="email"
              className="w-full rounded-xl bg-slate-950/70 border border-slate-700 px-3 py-2 text-sm text-slate-50 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-300">
              {t("common.displayName")}
            </label>
            <input
              type="text"
              autoComplete="name"
              className="w-full rounded-xl bg-slate-950/70 border border-slate-700 px-3 py-2 text-sm text-slate-50 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Milo & Partner"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-300">
              {t("common.password")}
            </label>
            <input
              type="password"
              required
              autoComplete="new-password"
              className="w-full rounded-xl bg-slate-950/70 border border-slate-700 px-3 py-2 text-sm text-slate-50 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>

          {error && (
            <div className="text-xs text-rose-400 bg-rose-950/40 border border-rose-800 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-pink-500 text-white text-sm font-semibold py-2.5 shadow-lg shadow-pink-500/30 hover:bg-pink-400 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? t("common.loading") : t("auth.registerButton")}
          </button>
        </form>

        <div className="text-xs text-slate-400 flex items-center justify-between pt-2">
          <span>{t("auth.hasAccount")}</span>
          <Link to="/login" className="text-pink-300 hover:text-pink-200 font-medium">
            {t("auth.loginCta")}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
